import { Providers } from "rivals/components/shared/VideoPlayer/types";
import { MEDIA_TYPES } from "rivals/shared/constants";
import { College } from "rivals/shared/interfaces/College";
import { Gallery } from "rivals/shared/interfaces/Content";
import { Prospect } from "rivals/shared/interfaces/Prospect";
import { ENTRY_TYPES, EXIT_TYPES } from "rivals/shared/interfaces/Transfer";
import { Position } from "../Position";

export interface CollegePlayer {
  athleteProfileAlt: string | null;
  athleteSlug: string;
  athleteUrl: string;
  careerStatistics: CareerStatistics;
  city: string;
  cloudinaryPublicId: string | null;
  college: College;
  collegeExperience: string;
  collegePlayerId: number;
  draftStatus: boolean;
  eligibilityYear: number;
  firstName: string;
  hasProspectProfileImage?: boolean;
  height: number;
  lastName: string;
  personId: number;
  position: Position;
  profileImageUrl?: string;
  stateAbbreviation?: string;
  stateId: number;
  stateName: string;
  teamLogo: string;
  teamPrimaryColor: string;
  transfers?: CollegeTransfer[];
  twitterCardDescription: string | null;
  twitterHandle: string | null;
  twitterImage: string | null;
  uniformNumber: number;
  weight: number;
  yahooPlayerId: string;
}

export interface AthleteImage {
  caption: string;
  credit: string;
  url: string;
}

export interface AthleteVideo {
  remoteId: string;
  source: keyof Providers;
}

export type AthleteMediaType =
  | MEDIA_TYPES.PHOTOS
  | MEDIA_TYPES.VIDEOS
  | MEDIA_TYPES.GALLERIES
  | MEDIA_TYPES.NEWS;

export interface AthleteFeaturedMedia {
  galleries: Gallery[];
  images: AthleteImage[];
  videos: AthleteVideo[];
}

export enum AthleteViews {
  COLLEGE_PLAYER_VIEW = "cv",
  PROSPECT_VIEW = "pv"
}

// CARD is for inline or hover cards on articles page or on global search
// PAGE is for athlete profile page
// SOCIAL_CARD is for athlete profile page preview on social media posts (twitter, facebook, reddit), or for iframe embed
export enum AthleteBioVariants {
  CARD = "card",
  PAGE = "page",
  SOCIAL_CARD = "socialCard"
}

export interface CollegeStat {
  name: string;
  stat: number;
}
export interface SeasonStats {
  stats: CollegeStat[];
  year: number | string;
}

export interface CareerStatistics {
  career: CollegeStat[];
  season: SeasonStats;
}
export type CollegePlayerGameData = Array<GamesTableData>;

export interface CollegeTransfer {
  athleticYear?: string;
  classYear?: number;
  collegePlayerId?: number;
  createdAt?: string | Date;
  enterDate?: string;
  entryType?: ENTRY_TYPES;
  exitCollegeColor?: string;
  exitCollegeId: number | null;
  exitCollegeLogo?: string;
  exitCollegeName: string | null;
  exitDate?: string;
  exitType?: EXIT_TYPES | null;
  id?: number;
  intentDate?: string;
  originCollegeColor?: string;
  originCollegeId?: number;
  originCollegeLogo?: string;
  originCollegeName?: string;
  ranked?: boolean;
  rivalsRating: number;
  stars: number;
}

export interface CollegeTransfers {
  collegeTransfers: CollegeTransfer[];
}

export interface CreateCollegeTransferParams {
  collegePlayerId: number;
  collegeTransfer: Omit<CollegeTransfer, "id">;
}

export interface UpdateCollegeTransferParams {
  classYear?: number;
  collegePlayerId?: number;
  enterDate?: string | null;
  entryType?: ENTRY_TYPES;
  exitCollegeId: number | null;
  exitDate?: string;
  exitType?: EXIT_TYPES | null;
  id?: number;
  intentDate?: string | null;
  originCollegeId?: number;
  rivalsRating: number;
  stars: number;
}

export interface GamesTableData {
  columns: Array<string>;
  data: Array<object>;
  tableTitle: string;
}

export interface GetCollegeTransferParams {
  cache?: RequestCache;
  collegePlayerId: number;
  collegeTransferId: number;
}

export type ProspectSlugParams = {
  playerSlug: string;
  type: "slug";
  year: string;
};

export type ProspectIdParams = {
  id: string;
  type: "id";
};

export type PersonIdParams = {
  includeGalleries?: boolean;
  personId: string;
  type: "personId";
};

export function isPersonIdParams(
  params: PersonIdParams | ProspectIdParams
): params is PersonIdParams {
  return (params as PersonIdParams).personId !== undefined;
}

export type AthletePageParams =
  | ProspectSlugParams
  | ProspectIdParams
  | PersonIdParams;

// TODO: refactor once we get actual data
export interface GameLog {
  date1: string;
  key: number;
  long2: number;
  opp1: string;
  rec2: number;
  score1: string;
  td1: number;
  td2: number;
  type1: string;
}

export interface YahooProfileMatch {
  playerDetails: {
    college: string;
    collegeLogo: string;
    displayName: string;
    experience: string;
    position: string;
  };
  yahooId?: string;
  yahooLink?: string;
}
export interface YahooProfileMatchData {
  firstMatch: YahooProfileMatch;
  similarMatches: YahooProfileMatch[];
}

export interface CollegePlayerRankingData {
  transferRank: {
    change: number;
    ordinality: number;
    url: string;
  };
  transferRating: {
    transferRating: number;
    transferStars: number;
  };
}

export interface YahooSyncableData {
  birthCity: string;
  collegeExperience: string;
  collegeId: number;
  collegeName: string;
  eligibilityYear: number;
  firstName: string;
  height: number;
  lastName: string;
  positionId: number;
  stateId: number;
  uniformNumber: number;
  weight: number;
}

export function isCollegePlayerType(
  player: CollegePlayer | Prospect
): player is CollegePlayer {
  return (player as CollegePlayer)?.yahooPlayerId !== undefined;
}
